import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";

import { EmptyState, Text } from "@performyard/py-components";

import type { CompensationAuditLogEntry } from "./types";

export type { CompensationAuditLogEntry };

type AuditLogProps = {
	title?: string;
	entries: CompensationAuditLogEntry[];
	emptyStateLabel?: string;
	/** When true, grow/shrink inside a flex parent (e.g. capped-height modal) instead of a fixed min height. */
	contained?: boolean;
};

/** Scrollport: block layout for entries so one vertical scrollbar owns the full stream (flex-col here caused nested scroll). */
const scrollAreaClass = [
	"box-border h-[320px] w-[384px] max-w-full shrink-0 overflow-x-hidden overflow-y-scroll px-[8px] pb-6 pt-[8px] text-pretty break-words scrollbar-gutter-stable",
	"[border-radius:var(--rounded-sm,4px)] border border-solid border-[var(--neutral-2,#E9EAEC)]",
	"[scrollbar-width:thin] [scrollbar-color:var(--neutral-3,#B7BBC2)_var(--neutral-2,#E9EAEC)]",
	"[&::-webkit-scrollbar]:[width:var(--spacing-1,4px)] [&::-webkit-scrollbar]:bg-[var(--neutral-2,#E9EAEC)]",
	"[&::-webkit-scrollbar-track]:bg-[var(--neutral-2,#E9EAEC)]",
	"[&::-webkit-scrollbar-thumb]:h-[113px] [&::-webkit-scrollbar-thumb]:[min-height:113px]",
	"[&::-webkit-scrollbar-thumb]:[border-radius:var(--rounded-full,384px)]",
	"[&::-webkit-scrollbar-thumb]:bg-[var(--neutral-3,#B7BBC2)]",
].join(" ");

const AuditLog = ({
	title = "Activity Stream",
	entries,
	emptyStateLabel = "No activity yet",
	contained = false,
}: AuditLogProps) => {
	const [animationParent] = useAutoAnimate();

	const hasEntries = entries.length > 0;

	const rootClass = contained
		? "flex min-h-0 flex-1 w-[384px] max-w-full flex-col overflow-hidden"
		: "flex w-[384px] max-w-full flex-col overflow-hidden";

	return (
		<div className={rootClass}>
			<div className="flex shrink-0 items-center justify-center bg-[var(--py-neutral-0)] p-2">
				<Text weight="semi-bold" size="sub-heading">
					{title}
				</Text>
			</div>
			<div className={scrollAreaClass}>
				{hasEntries ? (
					<div className="min-h-[calc(100%+1px)] w-full">
						<div className="flex w-full flex-col items-start justify-start gap-[8px]" ref={animationParent}>
							{entries.map((entry) => (
								<div key={entry.id} className="w-full">
									<div className="flex w-full flex-col items-start gap-1">
										<div className="w-full max-w-full overflow-x-auto rounded-lg bg-[var(--py-neutral-0)] p-2">
											{entry.content}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				) : (
					<div className="flex min-h-[calc(100%+1px)] w-full flex-col">
						<div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center [&_svg]:!h-[67px] [&_svg]:!w-[141px]">
							<EmptyState variant="illustration" option={8} label={emptyStateLabel} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default AuditLog;
